import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useMatch } from 'react-router';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import classNames from 'classnames';

import { getItemToLocalStorage, saveItemToLocalStorage } from '@/config';
import { isFeatureEnabled } from '@/configuration/featureToggle/featureToggleSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import InternalErrorState from '@/features/app/InternalErrorState';
import { useFetchBatteryLevelsQuery } from '@/features/battery/api/batterySlice';
import PanelBody from '@/features/battery/PanelBody';
import SohBanner from '@/features/battery/SohBanner';
import { getDaysInYearsAndMonths } from '@/features/battery/utils/DateUtils';
import BatteryHistoryGraph from '@/features/batteryHistoryGraph/BatteryHistoryGraph';
import { ACTIVE_SOH_STATUSES, LOW_MILEAGE, NO_DATA, NoData } from '@/features/batteryOverview/utils/BatteryDataUtils';
import { ExternalBatteryStateOfChargeStatus } from '@/generated/GeneratedApiTypes';
import PackDetailsButton from './PackDetailsButton';
import SoHValue from './SoHValue';
import { useBatteryOverviewTooltips } from './useBatteryOverviewTooltips';

const LoadingScreen = () => {
    return (
        <div data-testid="loading-screen" className="col-xs-12 col-sm-6 col-md-4 display-flex flex-column">
            {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
            <ContentLoader height={35} className={'margin-top-10 margin-bottom-10'} />
            <ContentLoader height={100} width={'100%'} className={'margin-top-10 margin-bottom-10'} />
        </div>
    );
};

const VIN_SEARCH_TOKEN_PARAM_NAME = 'vin_search_token';
const VIN_SEARCH_LOCAL_STORAGE_KEY_PREFIX = 'vin-search';
const KM = ' km';
const KWH = ' kWh';
const NO_VALUE = '-';

export const handleVinSearchToken = (assetId: string): string => {
    const vinSearchToken = new URLSearchParams(window.location.search).get(VIN_SEARCH_TOKEN_PARAM_NAME);

    if (vinSearchToken == null) {
        return getItemToLocalStorage(`${VIN_SEARCH_LOCAL_STORAGE_KEY_PREFIX}:${assetId}`) ?? '';
    }

    saveItemToLocalStorage(`${VIN_SEARCH_LOCAL_STORAGE_KEY_PREFIX}:${assetId}`, vinSearchToken);
    return vinSearchToken;
};

// eslint-disable-next-line max-len
const renderBatteryStatus = (
    status: ExternalBatteryStateOfChargeStatus | undefined,
    remaining_range: number | undefined,
) => {
    let glyphClassNames;
    switch (status) {
        case 'CRITICAL':
            // eslint-disable-next-line max-len
            glyphClassNames = classNames(
                'rioglyph rioglyph-battery-level-empty text-color-danger text-size-20 margin-right-10 rotate-90',
            );
            break;
        case 'WARNING':
            // eslint-disable-next-line max-len
            glyphClassNames = classNames(
                'rioglyph rioglyph-battery-level-low text-color-warning text-size-20 margin-right-10 rotate-90',
            );
            break;
        default: {
            glyphClassNames = classNames('rioglyph rioglyph-battery-level-full text-size-20 margin-right-10 rotate-90');
        }
    }

    return (
        <div className={'display-flex align-items-center margin-top-1'}>
            <div className="webfont-preview display-flex align-items-center">
                <span data-testid="battery-glyph" className={glyphClassNames} />
            </div>
            <span>
                {remaining_range}
                {'%'}
            </span>
        </div>
    );
};

const panelClassNames = classNames('panel panel-default margin-bottom-0 width-100pct');
const panelWithoutSohStatusClassNames = classNames('panel panel-default margin-bottom-0 width-100pct');
const displayFlex = classNames('display-flex');
const h5ClassName = classNames('h5 margin-bottom-5 margin-top-0 text-normal');
const marginTop20 = classNames('margin-top-20');
const textSize18 = classNames('text-size-18');
const textSize14 = classNames('text-size-14');

const BatteryOverview = () => {
    const intl = useIntl();
    const assetId = useMatch('/:assetId')?.params.assetId;
    const { isLoading, isError, error, data: batteryLevel, refetch } = useFetchBatteryLevelsQuery(assetId!);
    const systemStateOfHealthStatus =
        batteryLevel?.health_data?.system.state_of_health_status?.toUpperCase() ?? NO_DATA;
    const { fullChargeEnergyTooltip, energyThroughputTooltip, chargedEnergyFastChargeTooltip, sohTooltip } =
        useBatteryOverviewTooltips(isLoading, isError, systemStateOfHealthStatus);

    const isCbpVehicle = batteryLevel?.health_data?.system.battery_type?.includes('CBP');

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (isError) {
        return (
            <InternalErrorState
                errorCode={(error as FetchBaseQueryError).status as number}
                reloadTriggerFunction={refetch}
            />
        );
    }

    const dischargingEnergyThroughput = batteryLevel?.health_data?.system.discharging_energy_throughput;
    const mileage = batteryLevel?.health_data?.system.mileage;
    const remainingRange = batteryLevel?.monitoring_data?.remaining_range;

    const renderSoHValue = () => (
        <>
            {systemStateOfHealthStatus !== NO_DATA && <PanelBody date={batteryLevel?.health_data?.timestamp} />}
            <SoHValue
                status={systemStateOfHealthStatus}
                percentage={batteryLevel?.health_data?.system.state_of_health_percentage}
                noDataClassName="margin-top-20"
            />
        </>
    );

    return (
        <>
            <div className={`h4 text-medium margin-bottom-${systemStateOfHealthStatus === LOW_MILEAGE ? '15' : '25'}`}>
                <FormattedMessage id="intl-msg:batteryService.batteryOverview.title" />
            </div>

            {systemStateOfHealthStatus === LOW_MILEAGE && <SohBanner />}

            <div className={'equal-height row margin-bottom-20'}>
                <div className="col-xs-12 col-sm-6 col-md-6 display-flex">
                    <div data-testid={'component-panel-soh'} className={panelClassNames}>
                        <div className="panel-body">
                            <div className={displayFlex}>
                                <div className={h5ClassName}>
                                    <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.title" />
                                </div>
                                {ACTIVE_SOH_STATUSES.includes(systemStateOfHealthStatus) && (
                                    <span className={'margin-left-5'}>
                                        <OverlayTrigger overlay={sohTooltip} placement={'right'}>
                                            <span
                                                className="rioglyph rioglyph-exclamation-sign text-color-dark text-size-16
                                        width-auto margin-top-0"
                                            />
                                        </OverlayTrigger>
                                    </span>
                                )}
                            </div>
                            {renderSoHValue()}
                        </div>
                    </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6 display-flex">
                    <div data-testid={'component-panel-soc'} className={panelClassNames}>
                        <div className="panel-body">
                            <div className={displayFlex}>
                                <div className={h5ClassName}>
                                    <FormattedMessage id="intl-msg:batteryService.stateOfCharge" />
                                </div>
                            </div>
                            <div>
                                {batteryLevel?.monitoring_data == null ? (
                                    <div className={marginTop20}>
                                        <NoData />
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            data-testid="date-panel"
                                            className={'display-flex flex-wrap text-color-dark'}
                                        >
                                            <span className={textSize14}>
                                                {remainingRange !== undefined ? (
                                                    <>
                                                        {/* eslint-disable-next-line max-len */}
                                                        <FormattedMessage id="intl-msg:batteryService.remainingRange" />{' '}
                                                        <FormattedNumber value={remainingRange} />
                                                        {KM}
                                                    </>
                                                ) : (
                                                    NO_VALUE
                                                )}
                                            </span>
                                        </div>
                                        <div>
                                            {renderBatteryStatus(
                                                batteryLevel?.monitoring_data?.status,
                                                batteryLevel?.monitoring_data?.state_of_charge,
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6 display-flex margin-top-5">
                    <div data-testid={'component-panel-years-of-usage'} className={panelWithoutSohStatusClassNames}>
                        <div className="panel-body">
                            <div className={displayFlex}>
                                <div className={h5ClassName}>
                                    <FormattedMessage id="intl-msg:batteryService.batteryAge" />
                                </div>
                            </div>
                            <div className={marginTop20}>
                                <span className={textSize18}>
                                    {batteryLevel?.health_data?.system.usage_days !== undefined ? (
                                        getDaysInYearsAndMonths(batteryLevel.health_data.system.usage_days, intl)
                                    ) : (
                                        <NoData />
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6 display-flex margin-top-5">
                    <div data-testid={'component-panel-battery-mileage'} className={panelWithoutSohStatusClassNames}>
                        <div className="panel-body">
                            <div className={displayFlex}>
                                <div className={h5ClassName}>
                                    <FormattedMessage id="intl-msg:batteryService.totalMileage" />
                                </div>
                            </div>
                            <div className={marginTop20}>
                                <span className={textSize18}>
                                    {mileage !== undefined ? (
                                        <>
                                            <FormattedNumber value={mileage} />
                                            {KM}
                                        </>
                                    ) : (
                                        <NoData />
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6 display-flex margin-top-5">
                    <div className={panelWithoutSohStatusClassNames}>
                        <div className="panel-body">
                            <div className={displayFlex}>
                                <div className={h5ClassName}>
                                    <FormattedMessage id="intl-msg:batteryService.fullChargeEnergy" />
                                </div>
                                <span className={'margin-left-5'}>
                                    <OverlayTrigger overlay={fullChargeEnergyTooltip} placement={'right'}>
                                        <span
                                            className="rioglyph rioglyph-exclamation-sign text-color-dark text-size-16
                                        width-auto margin-top-0"
                                        />
                                    </OverlayTrigger>
                                </span>
                            </div>
                            <div className={marginTop20}>
                                <span className={textSize18}>
                                    {batteryLevel?.health_data?.system.full_charge_energy !== undefined ? (
                                        <>
                                            <FormattedNumber
                                                value={batteryLevel.health_data.system.full_charge_energy}
                                            />
                                            {KWH}
                                        </>
                                    ) : (
                                        <NoData />
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6 display-flex margin-top-5">
                    <div data-testid={'component-panel-energy-throughput'} className={panelWithoutSohStatusClassNames}>
                        <div className="panel-body">
                            <div className={displayFlex}>
                                <div className={h5ClassName}>
                                    <FormattedMessage id="intl-msg:batteryService.dischargedEnergyThroughput" />
                                </div>
                                <span className={'margin-left-5'}>
                                    <OverlayTrigger overlay={energyThroughputTooltip} placement={'right'}>
                                        <span
                                            className="rioglyph rioglyph-exclamation-sign text-color-dark text-size-16
                                        width-auto margin-top-0"
                                        />
                                    </OverlayTrigger>
                                </span>
                            </div>
                            <div className={marginTop20}>
                                <span className={textSize18}>
                                    {dischargingEnergyThroughput !== undefined ? (
                                        <>
                                            <FormattedNumber value={dischargingEnergyThroughput} />
                                            {KWH}
                                        </>
                                    ) : (
                                        <NoData />
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                {isCbpVehicle && (
                    <div className="col-xs-12 col-sm-6 col-md-6 display-flex margin-top-5">
                        <div className={panelWithoutSohStatusClassNames}>
                            <div className="panel-body">
                                <div className={displayFlex}>
                                    <div className={h5ClassName}>
                                        <FormattedMessage id="intl-msg:batteryService.chargedEnergyFastCharge" />
                                    </div>
                                    <span className={'margin-left-5'}>
                                        <OverlayTrigger overlay={chargedEnergyFastChargeTooltip} placement={'right'}>
                                            <span
                                                className="rioglyph rioglyph-exclamation-sign text-color-dark text-size-16
                                        width-auto margin-top-0"
                                            />
                                        </OverlayTrigger>
                                    </span>
                                </div>
                                <div className={marginTop20}>
                                    <span className={textSize18}>
                                        {batteryLevel?.health_data?.system.charged_energy_fast_charge !== undefined ? (
                                            <>
                                                <FormattedNumber
                                                    value={batteryLevel.health_data.system.charged_energy_fast_charge}
                                                />
                                                {KWH}
                                            </>
                                        ) : (
                                            <NoData />
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {ACTIVE_SOH_STATUSES.includes(systemStateOfHealthStatus) &&
                (batteryLevel?.health_data?.system?.packs_count ?? 0) > 2 && (
                    <PackDetailsButton assetId={batteryLevel!.health_data!.asset_id} />
                )}
            <BatteryHistoryGraph />
        </>
    );
};

export default BatteryOverview;
